/**
 * UI Assets API
 *
 * UI asset management endpoints for versioned image/config assets.
 * Corresponds to backend /v1/admin/ui-assets/*
 */

import apiClient from './client';
import type {
  CreateUiAssetRequest,
  UiAssetDetail,
  UiAssetListResponse,
  UpdateUiAssetRequest,
} from '@/types/uiAssets';

export const uiAssetsApi = {
  list: async (group?: string): Promise<UiAssetListResponse> => {
    const params = group ? { group } : {};
    const { data } = await apiClient.get('/ui-assets', { params });
    return data;
  },

  get: async (id: string): Promise<UiAssetDetail> => {
    const { data } = await apiClient.get(`/ui-assets/${id}`);
    return data;
  },

  create: async (req: CreateUiAssetRequest): Promise<UiAssetDetail> => {
    const { data } = await apiClient.post('/ui-assets', req);
    return data;
  },

  update: async (id: string, req: UpdateUiAssetRequest): Promise<UiAssetDetail> => {
    const { data } = await apiClient.put(`/ui-assets/${id}`, req);
    return data;
  },

  upload: async (id: string, file: File): Promise<UiAssetDetail> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post(`/ui-assets/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  rollback: async (id: string, version: number): Promise<UiAssetDetail> => {
    const { data } = await apiClient.post(`/ui-assets/${id}/rollback/${version}`);
    return data;
  },

  toggleRemote: async (id: string, enabled: boolean): Promise<UiAssetDetail> => {
    const { data } = await apiClient.put(`/ui-assets/${id}/toggle-remote`, null, {
      params: { enabled },
    });
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/ui-assets/${id}`);
  },
};
